import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaTruck,
  FaBolt,
  FaCalendarAlt,
  FaClock,
  FaTimesCircle,
  FaBoxOpen,
  FaChevronRight,
  FaFileInvoice,
  FaSyncAlt,
  FaCartPlus,
  FaUndo,
} from "react-icons/fa";
import { buildAssetUrl } from "../../../../utils/api";
import { getProductPath } from "../../../../utils/productSlug";
import { fetchAndDownloadInvoice } from "../../../../utils/invoiceGenerator";
import {
  STATUS_CONFIG,
  PAYMENT_CONFIG,
  PAYMENT_STATUS_CONFIG,
  DOT,
  formatDate,
  formatRs,
} from "./orderConstants";

/* ─── ProductThumb ──────────────────────────────────────────── */
// Uses React state so the fallback icon cleanly replaces a broken image.
// buildAssetUrl() handles both relative paths ("public/assets/...") and
// absolute URLs - it always prepends REACT_APP_API_URL when needed.

const ProductThumb = ({ src, name }) => {
  const [failed, setFailed] = useState(false);
  const url = buildAssetUrl(src);

  return (
    <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-green-50 to-emerald-100">
      {url && !failed ? (
        <img
          src={url}
          alt={name}
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <FaBoxOpen className="h-5 w-5 text-emerald-400" />
      )}
    </div>
  );
};

/* ─── OrderCard ─────────────────────────────────────────────── */

const OrderCard = ({ order, onCancel, onRequestReturnItem, onDownloadInvoice, onReorder }) => {
  const navigate = useNavigate();
  const [showAllItems, setShowAllItems] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [reordering, setReordering] = useState(false);

  // Field mapping: new API shape (order_status, placed_at, ...) with
  // fallbacks to old shape (status, created_at, ...) so nothing breaks.
  const orderId      = order.order_id ?? order.id;
  const orderNumber  = order.order_number ?? `#${orderId}`;
  const rawStatus    = (
    String(order.status || "").toUpperCase() === "CANCELLED" ||
    String(order.order_status || "").toUpperCase() === "CANCELLED"
      ? "CANCELLED"
      : (order.order_status ?? order.status ?? "PENDING")
  ).toUpperCase();
  const statusCfg    = STATUS_CONFIG[rawStatus] ?? {
    label: rawStatus.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase()),
    bg: "bg-gray-50",
    text: "text-gray-600",
    border: "border-gray-200",
    dot: "bg-gray-400",
    accentBar: "bg-gray-400",
    Icon: FaClock,
  };
  const StatusIcon   = statusCfg.Icon;

  const paymentMethod = (order.payment_method ?? "").toUpperCase();
  const paymentCfg    = PAYMENT_CONFIG[paymentMethod] ?? {
    label: paymentMethod || "N/A",
    Icon: FaBoxOpen,
    color: "text-gray-600",
  };
  const PayIcon = paymentCfg.Icon;

  const rawPayStatus = (order.payment_status ?? "").toUpperCase();
  const payStatusCfg = PAYMENT_STATUS_CONFIG[rawPayStatus] ?? {
    label: rawPayStatus || "-",
    bg: "bg-gray-50",
    text: "text-gray-600",
  };

  const totalAmount     = parseFloat(order.total_amount ?? order.total ?? 0);
  const deliveryFee     = parseFloat(order.delivery_fee ?? 0);
  const placedAt        = order.placed_at ?? order.created_at;
  const deliveryName    = order.delivery_name ?? "";
  const deliveryCity    = order.delivery_city ?? "";
  const deliveryPincode = order.delivery_pincode ?? "";
  const deliveryMobile  = order.delivery_mobile ?? "";
  const deliveryType    = (order.delivery_type ?? "STANDARD").toUpperCase();

  // Extract scheduled delivery slot text dynamically
  const slotText =
    order.delivery_slot ||
    order.delivery_slot_name ||
    order.slot_name ||
    order.time_slot ||
    order.delivery_time_slot ||
    order.delivery_time ||
    order.slot ||
    (order.notes && order.notes.includes("Time slot:")
      ? order.notes.match(/Time slot:\s*([^|]+)/i)?.[1]?.trim()
      : "") ||
    "";

  const isScheduled =
    deliveryType === "SCHEDULED" ||
    Boolean(slotText) ||
    String(order.delivery_option || "").toUpperCase() === "SCHEDULED";

  // Clean note for display without raw "Time slot: ..."
  const userNotes =
    order.clean_notes !== undefined
      ? order.clean_notes
      : (order.notes || "").replace(/Time slot:\s*[^|]+(\s*\|\s*)?/i, "").trim();

  const getDeliveryConfig = (type) => {
    const upper = String(type || "STANDARD").toUpperCase();
    if (isScheduled) {
      return {
        label: "Scheduled",
        icon: FaCalendarAlt,
        textClass: "text-purple-600",
      };
    }
    switch (upper) {
      case "EXPRESS":
        return {
          label: "Express",
          icon: FaBolt,
          textClass: "text-amber-600",
        };
      case "SCHEDULED":
        return {
          label: "Scheduled",
          icon: FaCalendarAlt,
          textClass: "text-purple-600",
        };
      case "STANDARD":
      default:
        return {
          label: upper ? upper.charAt(0) + upper.slice(1).toLowerCase() : "Standard",
          icon: FaTruck,
          textClass: "text-emerald-600",
        };
    }
  };

  const deliveryConfig = getDeliveryConfig(deliveryType);
  const DeliveryIcon = deliveryConfig.icon;

  const items        = order.items ?? [];
  const PREVIEW_LIMIT = 3;
  const previewItems = showAllItems ? items : items.slice(0, PREVIEW_LIMIT);
  const extraCount   = items.length - PREVIEW_LIMIT;

  const canCancel = !["CANCELLED", "DELIVERED"].includes(rawStatus);

  const canReturn = rawStatus === "DELIVERED";

  return (
    <div className="group relative overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-all duration-300 hover:border-gray-200 hover:shadow-lg">

      {/* Colored top accent bar - changes colour by order status */}
      <div className={`h-1 w-full ${statusCfg.accentBar}`} />

      <div className="p-5">

        {/* ── Header row ── */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Order</p>
            <p className="mt-0.5 font-mono text-sm font-extrabold tracking-wider text-gray-900">
              {orderNumber}
            </p>
            <p className="mt-1 text-[11px] text-gray-400">{formatDate(placedAt)}</p>
          </div>

          {/* Status pill */}
          <span
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-bold ${statusCfg.bg} ${statusCfg.text} ${statusCfg.border}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${statusCfg.dot}`} />
            <StatusIcon className="h-2.5 w-2.5" />
            {statusCfg.label}
          </span>
        </div>

        {/* ── Items preview ── */}
        {items.length > 0 && (
          <div className="mt-4 space-y-2.5">
            {previewItems.map((item, idx) => {
              const qty       = parseInt(item.quantity ?? item.qty ?? 1, 10);
              const name      = item.product_name ?? item.name ?? "Product";
              const variantName = String(item.variant_name ?? "").trim();
              const unitName    = String(item.unit_name ?? "").trim();
              const variant     = variantName && unitName
                ? `${variantName} ${unitName}`
                : variantName || unitName;
              const unitPrice = parseFloat(item.unit_price ?? item.price ?? 0);

              // Build the subtitle line: "1 kg · x2 · Rs.27.00 each"
              const parts = [];
              if (variant) parts.push(variant);
              parts.push(`x${qty}`);
              if (unitPrice > 0) parts.push(`${formatRs(unitPrice)} each`);

              const productPath = getProductPath({
                product_id: item.product_id,
                product_slug: item.product_slug ?? item.slug,
                product_name: name,
              });

              return (
                <div key={item.order_item_id ?? item.product_id ?? idx} className="flex items-center gap-3">
                  {/* Image - clickable, navigates to product detail */}
                  <button
                    type="button"
                    onClick={() => productPath && navigate(productPath)}
                    className={`shrink-0 rounded-xl overflow-hidden transition ${
                      productPath ? "cursor-pointer hover:opacity-80 hover:ring-2 hover:ring-emerald-400 active:scale-95" : "cursor-default"
                    }`}
                    title={productPath ? `View ${name}` : name}
                  >
                    <ProductThumb src={item.product_image} name={name} />
                  </button>

                  <div className="min-w-0 flex-1">
                    {/* Product name - clickable */}
                    {productPath ? (
                      <button
                        type="button"
                        onClick={() => navigate(productPath)}
                        className="truncate text-sm font-semibold text-gray-900 hover:text-emerald-700 hover:underline transition text-left w-full"
                        title={`View ${name}`}
                      >
                        {name}
                      </button>
                    ) : (
                      <p className="truncate text-sm font-semibold text-gray-900">{name}</p>
                    )}
                    <p className="text-[11px] text-gray-400">{parts.join(` ${DOT} `)}</p>
                  </div>

                  <div className="shrink-0">
                    <p className="text-sm font-bold text-gray-800">
                      {formatRs(unitPrice * qty)}
                    </p>
                  </div>
                </div>
              );
            })}

            {extraCount > 0 && (
              <button
                type="button"
                onClick={() => setShowAllItems((prev) => !prev)}
                className="ml-14 mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-[#046a38] hover:underline focus:outline-none transition"
              >
                {showAllItems ? (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" /></svg>
                    Show less
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                    +{extraCount} more item{extraCount > 1 ? "s" : ""}
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {items.length === 0 && (
          <div className="mt-4 flex items-center gap-2 rounded-xl bg-gray-50 px-3 py-2.5 text-xs text-gray-500">
            <FaBoxOpen className="h-4 w-4 text-gray-300" />
            Order items loading...
          </div>
        )}

        <div className="my-4 border-t border-dashed border-gray-100" />

        {/* ── Info grid: Total / Payment / Pay Status / Delivery ── */}
        <div className="grid grid-cols-2 gap-2 text-[11px] sm:grid-cols-4">
          <div className="rounded-xl bg-gray-50 px-3 py-2.5">
            <p className="font-bold uppercase tracking-wider text-gray-400">Total</p>
            <p className="mt-0.5 text-sm font-black text-gray-900">{formatRs(totalAmount)}</p>
            {deliveryFee > 0 && (
              <p className="mt-0.5 text-[10px] text-gray-400">Delivery: {formatRs(deliveryFee)}</p>
            )}
          </div>

          <div className="rounded-xl bg-gray-50 px-3 py-2.5">
            <p className="font-bold uppercase tracking-wider text-gray-400">Payment</p>
            <p className={`mt-0.5 flex items-center gap-1 font-semibold ${paymentCfg.color}`}>
              <PayIcon className="h-3 w-3" />
              {paymentCfg.label}
            </p>
          </div>

          <div className="rounded-xl bg-gray-50 px-3 py-2.5">
            <p className="font-bold uppercase tracking-wider text-gray-400">Pay Status</p>
            <span
              className={`mt-0.5 inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold ${payStatusCfg.bg} ${payStatusCfg.text}`}
            >
              {payStatusCfg.label}
            </span>
          </div>

          <div className="rounded-xl bg-gray-50 px-3 py-2.5">
            <p className="font-bold uppercase tracking-wider text-gray-400">Delivery</p>
            <p
              className={`mt-0.5 flex items-center gap-1 font-semibold ${
                isScheduled ? "text-purple-600" : deliveryConfig.textClass
              }`}
            >
              {isScheduled ? <FaCalendarAlt className="h-3 w-3" /> : <DeliveryIcon className="h-3 w-3" />}
              {deliveryConfig.label}
            </p>
            {slotText ? (
              <p
                className="mt-0.5 truncate text-[10px] font-bold text-purple-700"
                title={slotText}
              >
                {slotText}
              </p>
            ) : null}
          </div>
        </div>

        {/* ── Delivery address snippet ── */}
        {(deliveryName || deliveryCity) && (
          <div className="mt-3 flex items-start gap-2 rounded-xl bg-emerald-50/60 px-3 py-2.5">
            <FaMapMarkerAlt className="mt-0.5 h-3 w-3 shrink-0 text-emerald-500" />
            <div className="min-w-0 text-[11px]">
              {deliveryName && (
                <span className="font-semibold text-gray-800">{deliveryName}</span>
              )}
              {deliveryCity && (
                <span className="text-gray-500">
                  {deliveryName ? ` ${DOT} ` : ""}
                  {deliveryCity}
                  {deliveryPincode ? ` - ${deliveryPincode}` : ""}
                </span>
              )}
              {deliveryMobile && (
                <span className="ml-2 inline-flex items-center gap-1 text-gray-400">
                  <FaPhoneAlt className="h-2.5 w-2.5" />
                  {deliveryMobile}
                </span>
              )}
            </div>
          </div>
        )}

        {/* ── Delivery Type & Time Slot banner ── */}
        <div
          className={`mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl px-3.5 py-2.5 text-xs ${
            isScheduled
              ? "border border-purple-200 bg-purple-50/70 text-purple-900"
              : deliveryType === "EXPRESS"
              ? "border border-amber-200 bg-amber-50/70 text-amber-900"
              : "border border-emerald-200 bg-emerald-50/70 text-emerald-900"
          }`}
        >
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <div className="flex items-center gap-1.5 font-bold">
              <DeliveryIcon className="h-3.5 w-3.5 shrink-0" />
              <span className="text-gray-500 font-semibold">Type:</span>
              <span className="font-extrabold">{deliveryConfig.label} Delivery</span>
            </div>

            {slotText && (
              <div className="flex items-center gap-1.5">
                <span className="text-purple-300">•</span>
                <span className="text-purple-700 font-semibold">Time Slot:</span>
                <span className="rounded-md bg-white px-2 py-0.5 font-extrabold text-purple-800 border border-purple-200 shadow-2xs">
                  {slotText}
                </span>
              </div>
            )}
          </div>

        </div>

        {/* ── Order Notes ── */}
        {userNotes && (
          <div className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50/70 border border-amber-100 px-3 py-2 text-xs text-amber-950">
            <span className="shrink-0 font-bold text-amber-800">📝 Notes:</span>
            <span className="font-medium italic text-amber-900">"{userNotes}"</span>
          </div>
        )}

        {/* ── Action buttons ── */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {/* ── Reorder ── */}
          <button
            type="button"
            disabled={reordering}
            onClick={async () => {
              if (reordering) return;
              setReordering(true);
              try {
                if (onReorder) await onReorder(orderId);
              } finally {
                setReordering(false);
              }
            }}
            className="inline-flex items-center gap-1.5 rounded-xl border border-[#046a38] bg-[#046a38] px-3.5 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-[#035530] active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
            title="Add all items from this order to your cart"
          >
            {reordering ? (
              <FaSyncAlt className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <FaCartPlus className="h-3.5 w-3.5" />
            )}
            {reordering ? "Adding…" : "Reorder"}
          </button>

          {canCancel && (
            <button
              onClick={() => onCancel(orderId)}
              className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 px-3.5 py-2 text-xs font-semibold text-red-600 transition hover:bg-red-50 active:scale-95"
            >
              <FaTimesCircle className="h-3 w-3" />
              Cancel
            </button>
          )}

          <button
            type="button"
            disabled={downloadingInvoice}
            onClick={async () => {
              if (downloadingInvoice) return;
              const targetOrderId = order.order_id ?? order.id ?? orderId;
              if (!targetOrderId) {
                toast.error("Order ID not found for invoice");
                return;
              }
              setDownloadingInvoice(true);
              try {
                if (onDownloadInvoice) {
                  await onDownloadInvoice(targetOrderId);
                } else {
                  await fetchAndDownloadInvoice(targetOrderId);
                }
              } finally {
                setDownloadingInvoice(false);
              }
            }}
            className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-2 text-xs font-bold text-emerald-800 transition hover:bg-emerald-100 hover:border-emerald-300 active:scale-95 disabled:opacity-50"
            title="Download Tax Invoice"
          >
            {downloadingInvoice ? (
              <FaSyncAlt className="h-3.5 w-3.5 animate-spin text-emerald-700" />
            ) : (
              <FaFileInvoice className="h-3.5 w-3.5 text-emerald-700" />
            )}
            {downloadingInvoice ? "Fetching..." : "Download Invoice"}
          </button>

          {canReturn && (
            <button
              type="button"
              onClick={() => {
                if (onRequestReturnItem) {
                  onRequestReturnItem(order);
                } else {
                  const targetOrderId = order.order_id ?? order.id ?? orderId;
                  navigate(`/returns/request?orderId=${targetOrderId}`, {
                    state: { order },
                  });
                }
              }}
              className="inline-flex items-center gap-1.5 rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-2 text-xs font-bold text-amber-900 transition hover:bg-amber-100 hover:border-amber-400 active:scale-95"
              title="Request Return / Refund for this order"
            >
              <FaUndo className="h-3 w-3 text-amber-700" />
              Return / Refund
            </button>
          )}

          <button
            onClick={() => navigate(`/account/track?orderId=${orderId}`)}
            className="ml-auto inline-flex items-center gap-1 rounded-xl border border-gray-200 px-3.5 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 active:scale-95"
          >
            Track Order
            <FaChevronRight className="h-2.5 w-2.5" />
          </button>
        </div>

      </div>
    </div>
  );
};

export default OrderCard;
